import { createFileRoute, Link } from "@tanstack/react-router";
import { getTaxonomy, type TopicCluster } from "@/lib/taxonomy.functions";
import { TERRITORIES } from "@/lib/taxonomy";
import { SITE_URL, absoluteUrl } from "@/lib/site";
import { breadcrumbs, itemList, ldScript, orgRef, pageNode, personRef, seoMeta } from "@/lib/schema";

const TITLE = "Topics | Buddha KA & KAfrequency Knowledge Hubs";
const DESCRIPTION =
  "Every subject taught across the KAfrequency canon — meditation, consciousness, frequency, breathwork, tai chi, Kemetic studies, Buddhism, martial arts, spiritual science, music production, and beat licensing.";

export const Route = createFileRoute("/topics/")({
  loader: async () => ({ topics: await getTaxonomy() }),
  component: TopicsIndex,
  head: ({ loaderData }) => {
    const topics = loaderData?.topics ?? [];
    return {
      ...seoMeta({ title: TITLE, description: DESCRIPTION, path: "/topics" }),
      links: [{ rel: "canonical", href: absoluteUrl("/topics") }],
      scripts: [
        ldScript(
          pageNode({
            type: "CollectionPage",
            id: `${SITE_URL}/topics#topics`,
            url: `${SITE_URL}/topics`,
            name: "Topics",
            description: DESCRIPTION,
            about: [personRef(), orgRef()],
            extra: {
              publisher: orgRef(),
              ...(topics.length
                ? { mainEntity: itemList(topics.map((t) => ({ path: `/topics/${t.slug}`, name: t.name }))) }
                : {}),
            },
          }),
        ),
        ldScript(breadcrumbs([["Topics", "/topics"]])),
      ],
    };
  },
});

function TopicsIndex() {
  const { topics } = Route.useLoaderData() as { topics: TopicCluster[] };

  return (
    <section className="mx-auto max-w-6xl px-4 py-20 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Knowledge Ecosystem</p>
      <h1 className="mt-4 font-serif text-4xl sm:text-5xl md:text-6xl">Topics</h1>
      <p className="mt-4 max-w-2xl text-muted-foreground">{DESCRIPTION}</p>

      <section aria-labelledby="authority-territories" className="mt-14">
        <h2 id="authority-territories" className="text-xs uppercase tracking-[0.3em] text-gold">
          The six authority territories
        </h2>
        <ul className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {TERRITORIES.map((t) => (
            <li key={t.slug} className="rounded-2xl border border-gold/30 bg-charcoal p-6">
              <Link to="/topics/$slug" params={{ slug: t.slug }} className="font-serif text-xl text-ivory hover:text-gold-luminous">
                {t.name}
              </Link>
              <p className="mt-2 text-sm text-muted-foreground">{t.definition}</p>
            </li>
          ))}
        </ul>
      </section>

      <h2 className="mt-16 text-xs uppercase tracking-[0.3em] text-gold">All topics</h2>
      <ul className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {topics.map((t) => (
          <li key={t.slug} className="rounded-2xl border hairline bg-charcoal/50 p-6">
            <Link to="/topics/$slug" params={{ slug: t.slug }} className="hover-glow">
              <h3 className="font-serif text-2xl text-ivory hover:text-gold-luminous">{t.name}</h3>
            </Link>
            <p className="mt-2 text-sm text-muted-foreground">{t.definition}</p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-gold">
              {t.total} linked {t.total === 1 ? "work" : "works"}
            </p>
          </li>
        ))}
      </ul>

      {topics.length === 0 && (
        <p className="mt-16 text-muted-foreground">Topic clusters are temporarily unavailable.</p>
      )}
    </section>
  );
}
